import { useId, type KeyboardEvent, type ReactNode } from 'react'
import { cx } from './cx'
import { Icon } from './Icon'

export interface ButtonGroupOption {
  value: string
  label: ReactNode
  icon?: string | undefined
}

type ButtonGroupBase = {
  'aria-label': string
  options: readonly ButtonGroupOption[]
  /** md = S (40dp), sm = XS (32dp). */
  size?: 'md' | 'sm' | undefined
  className?: string | undefined
}
export type ButtonGroupProps = ButtonGroupBase &
  (
    | { multi?: false | undefined; value: string; onChange: (value: string) => void }
    | { multi: true; value: readonly string[]; onChange: (value: string[]) => void }
  )

/**
 * Connected button group M3 Expressive (sostituisce il segmented button, "non piu' raccomandato").
 * Selezione singola = radiogroup (frecce, Home, End, tabindex roving); multipla = gruppo di toggle (aria-pressed).
 */
export function ButtonGroup(props: ButtonGroupProps) {
  const { options, className, size = 'md' } = props
  const uid = useId()

  const isSelected = (v: string) => (props.multi ? props.value.includes(v) : props.value === v)

  const choose = (v: string) => {
    if (props.multi) {
      props.onChange(props.value.includes(v) ? props.value.filter((x) => x !== v) : [...props.value, v])
    } else {
      props.onChange(v)
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (props.multi) return
    let next = -1
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (index + 1) % options.length
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (index - 1 + options.length) % options.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = options.length - 1
    if (next < 0) return
    e.preventDefault()
    const opt = options[next]
    if (!opt) return
    props.onChange(opt.value)
    document.getElementById(`${uid}-${next}`)?.focus()
  }

  const selectedIndex = options.findIndex((o) => isSelected(o.value))

  return (
    <div
      role={props.multi ? 'group' : 'radiogroup'}
      aria-label={props['aria-label']}
      className={cx('md-button-group', size === 'sm' && 'md-button-group--sm', className)}
    >
      {options.map((o, i) => {
        const on = isSelected(o.value)
        const tabbable = props.multi ? true : selectedIndex < 0 ? i === 0 : on
        return (
          <button
            key={o.value}
            id={`${uid}-${i}`}
            type="button"
            role={props.multi ? undefined : 'radio'}
            aria-checked={props.multi ? undefined : on}
            aria-pressed={props.multi ? on : undefined}
            tabIndex={tabbable ? 0 : -1}
            className={cx('md-button-group__item', on && 'md-button-group__item--selected', 'md-state', 'md-touch')}
            onClick={() => choose(o.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {o.icon ? <Icon name={o.icon} size={20} /> : null}
            <span>{o.label}</span>
          </button>
        )
      })}
    </div>
  )
}

/** Alias di compatibilita' (stessa API): i file che usavano SegmentedButton non cambiano. */
export const SegmentedButton = ButtonGroup
export type SegmentedButtonProps = ButtonGroupProps
export type SegmentedOption = ButtonGroupOption
